import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IHttpConnectionOptions,
  LogLevel,
  IRetryPolicy, RetryContext,
} from '@microsoft/signalr';
import Emittery from 'emittery';
import { inject } from 'inversify';
import { DateTime } from 'luxon';

import { HYBRID_MESSAGE, HYBRID_METHOD_TO_MTYPE } from './MessageTypes/ISignalRHybridMessages';

import { service } from '@/decorators/service';
import { Connection } from '@/services/Connection';
import { Environment } from '@/services/Environment';
import { StatusMsgContext } from '@/services/StatusMsgContext';
import { DeferredMessage } from '@/services/signalr/DeferredMessage';
import { SignalrSession } from '@/services/signalr/SignalrSession';
import { SignalrTimeoutError } from '@/services/signalr/SignalrTimeoutError';
import {
  IMessageType,
  InferMessageType,
  InferMessageTypeResponse,
  ISignalR,
  ISignalREventTypes,
  ISignalRInvokeOptions,
  ISignalRSession,
} from '@/services/signalr/types';
import { DeferredTimeoutError } from '@/utils/deferredPromise';
import { METRIC_THRESHOLDS } from '@/utils/metricsConstants';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';

export const SignalrMethodName = Symbol.for('SignalrMethodName');

//  Threshold for emitting signalr.invoke_rtt — only track slow invocations to limit noise
const SIGNALR_RTT_SLOW_THRESHOLD_MS = METRIC_THRESHOLDS.SIGNALR_INVOKE_SLOW_MS;

type LogRocketLike = {
  track?: (eventName: string, props?: Record<string, unknown>) => void;
};

function getLogRocket(): LogRocketLike | undefined {
  return (window as unknown as { LogRocket?: LogRocketLike }).LogRocket;
}

function trackInvokeRtt(requestName: string, durationMs: number, success: boolean, timedOut: boolean): void {
  const lr = getLogRocket();
  if (lr && typeof lr.track === 'function') {
    lr.track('signalr.invoke_rtt', {
      requestName,
      durationMs,
      success,
      timedOut,
    });
  }
}

@service(Signalr)
export class Signalr extends Emittery<ISignalREventTypes> implements ISignalR, IRetryPolicy {
  // Services.
  @inject(Environment) environment: Environment;
  @inject(Connection) connection: Connection;
  @inject(StatusMsgContext) statusMsgContext: StatusMsgContext;

  // Properties.
  readonly hubConnection: HubConnection = new HubConnectionBuilder().withUrl('https://localhost').build();
  readonly session: SignalrSession = new SignalrSession('', this.hubConnection);

  private boundEmitError = this.emitError.bind(this);
  private deferredMessages: Map<string, DeferredMessage> = new Map();
  private retryDelays = [0, 2, 10, 30, 60, 90, 120];

  private static readonly DEFAULT_RETRY_DELAYS = [0, 2, 10, 30, 60, 90, 120];

  /** Exposes the last retry context for observability (e.g., LogRocket tracking). */
  lastRetryContext: RetryContext | undefined;

  static defaultOptions: IHttpConnectionOptions = {
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
  };

  // Getters.
  get isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  // Methods.
  protected emitError(error?: Error) {
    this.emit('error', error).catch((e) => {
      // Intentional console error as an absolute last resort so the error doesn't get swallowed.
      // eslint-disable-next-line no-console
      console.error(error, e);
    });
  }

  // custom retry logic, implements IRetryPolicy
  nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    this.lastRetryContext = retryContext;

    if (this.retryDelays.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const delay = this.retryDelays.shift()!;
      const jitter = delay * 0.1 * (Math.random() * 2 - 1); // ±10% randomness
      return (delay + jitter) * 1000;
    }

    this.emit('fatal').catch(this.boundEmitError);
    return null;
  }

  // Invokes a method on the server.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async invoke<T extends InferMessageType<IMessageType<IDispatchMessage, any>> & { [SignalrMethodName]?: string }>(
    messageType: T,
    data?: object,
    options?: ISignalRInvokeOptions,
  ): Promise<InferMessageTypeResponse<T>> {
    const invokeStartMs = performance.now();
    const args: string[] = [];

    const requestName = this.connection.isHybrid
      ? messageType.hybridRequestName ?? messageType.requestName
      : messageType.requestName;

    // The name used for local event emission for outgoing requests.
    // In hybrid mode, the wire request name can be an incoming message type (e.g. TripUpdateMsg).
    // Emitting that name would incorrectly trigger incoming handlers on the client.
    const emitRequestName = messageType.requestName;

    const responseName = this.connection.isHybrid
      ? messageType.hybridResponseName ?? messageType.responseName
      : messageType.responseName;

    const processMessage = options?.processMessage ?? messageType.processMessage !== false;
    const emitRequest = options?.emitRequest !== false;

    const message = new DeferredMessage<InferMessageTypeResponse<T>>({
      mType: requestName,
      destinationId: this.session.companyId,
      ownerId: this.session.companyId,
      sourceId: this.session.userKey,
      userKey: this.session.userKey,
      sentTime: DateTime.now().toUTC().toISO(),
      ...(data ?? {}),
    });

    // Store short-lived request context by correlationKey for downstream StatusMsg translation/toasts.
    // This avoids mutating/attaching request payloads inside the transport layer.
    this.statusMsgContext.set(message.id, message.payload, 60000, options?.suppressSuccessToast);

    // Only use the `ProcessMessage` method name if the message type is not a legacy request. A legacy request is
    // a message type that also used its name as the SignalR method name. This was common practice prior to
    // establishing a singular `ProcessMessage` SignalR method that handles everything on the BE.
    // @todo Refactor once we no longer support the "legacy" API.
    if (processMessage) {
      args.push('ProcessMessage');
    }

    // Append the request name.
    args.push(requestName);

    // Append the message.
    args.push(message.toString());

    // Append any additional args.
    if (options?.args) {
      args.push(...options.args);
    }

    // If the message type indicates there should be a response, defer the message.
    if (responseName) {
      this.deferredMessages.set(message.id, <DeferredMessage><unknown>message);

      const method = (responseName).toLowerCase();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const methods = (this.hubConnection.methods ?? {}) as Record<string, (() => any)[]>;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      if (methods[method] === undefined || methods[method].length <= 0) {
        this.hubConnection.on(method, this.onProcessMessage.bind(this));
      }
    }

    try {
      if (emitRequest) {
        // Emit a generic event for all outgoing messages.
        await this.emit('outgoing', <IDispatchMessage><unknown> message.payload);

        // Message names are used to emit events, which are strongly typed.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await this.emit(emitRequestName, <IDispatchMessage><unknown> message.payload);
      }

      // Invoke method.
      await this.hubConnection.invoke(args[0], ...args.slice(1));

      // If the MessageType indicates there should not be a response, immediately resolve it as an empty object.
      if (!responseName) {
        message.deferred.resolve(<InferMessageTypeResponse<T>>{});
      }
    }
    catch (e) {
      if (responseName) {
        this.deferredMessages.delete(message.id);
      }
      message.deferred.reject(e);
    }

    // Attempt to resolve the deferred promise.
    try {
      await message.deferred.promise;
    }
    catch (e) {
      const invokeDurationMs = performance.now() - invokeStartMs;

      //  Track slow or failed invocations
      if (invokeDurationMs >= SIGNALR_RTT_SLOW_THRESHOLD_MS || e instanceof DeferredTimeoutError) {
        trackInvokeRtt(requestName, Math.round(invokeDurationMs), false, e instanceof DeferredTimeoutError);
      }

      // Catch the deferred timeout error and be more specific about the message that timed out.
      if (e instanceof DeferredTimeoutError && responseName) {
        this.deferredMessages.delete(message.id);
        return Promise.reject(new SignalrTimeoutError(responseName));
      }

      if (responseName) {
        this.deferredMessages.delete(message.id);
      }

      // Return the reject the error (re-throw).
      return Promise.reject(e);
    }

    //  Track slow successful invocations
    const invokeDurationMs = performance.now() - invokeStartMs;
    if (invokeDurationMs >= SIGNALR_RTT_SLOW_THRESHOLD_MS) {
      trackInvokeRtt(requestName, Math.round(invokeDurationMs), true, false);
    }

    // Return the resolved deferred promise.
    return message.deferred.promise;
  }

  public isMessageDeferred(message?: IDispatchMessage): boolean {
    if (message === undefined) {
      return false;
    }

    const correlationKey = typeof message?.correlationKey === 'string' ? message.correlationKey : undefined;
    return correlationKey ? this.deferredMessages.has(correlationKey) : false;
  }

  protected onProcessMessage(message: IDispatchMessage) {
    const correlationKey = typeof message?.correlationKey === 'string' ? message.correlationKey : undefined;

    // Emit a generic event for all incoming messages.
    this.emit('incoming', message).catch(this.boundEmitError);

    // Emit message type events so consumers listening via `SignalrMessage(...)` fire.
    if (message?.mType) {
      this.emit(message.mType, <IDispatchMessage><unknown> message).catch(this.boundEmitError);
    }

    if (correlationKey) {
      const deferred = this.deferredMessages.get(correlationKey);
      if (deferred) {
        deferred.deferred.resolve(message);
        this.deferredMessages.delete(correlationKey);
      }
    }
  }

  protected registerHybridMessages() {
    // Register hybrid message handlers for all backend hybrid methods
    HYBRID_MESSAGE.forEach((messageName) => {
      this.hubConnection.on(messageName.toLowerCase(), (payload: Record<string, unknown>) => {
        const messageTypeFromPayload = typeof payload?.mType === 'string' ? payload.mType : undefined;

        const mType = messageTypeFromPayload ?? HYBRID_METHOD_TO_MTYPE[messageName];
        if (payload.mType === undefined) {
          payload.mType = mType;
        }
        this.onProcessMessage(payload as unknown as IDispatchMessage);
      });
    });
  }

  async start(url: string, options?: IHttpConnectionOptions): Promise<ISignalRSession> {
    // Stop any existing connection.
    await this.stop();

    // Ensure default options.
    const opts: IHttpConnectionOptions = {
      ...(this.constructor as typeof Signalr).defaultOptions,
      ...options,
    };

    // Save method listeners that were registered prior to the connection being started.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const methods = (this.hubConnection.methods ?? {}) as Record<string, (() => any)[]>;
    (this.hubConnection as unknown) = new HubConnectionBuilder()
      .withUrl(url, opts)
      .withAutomaticReconnect(this)
      .configureLogging(opts.logger ?? (this.environment.isProd ? LogLevel.Error : LogLevel.Warning))
      .build();

    // Replace SignalR's methods with a proxy of existing methods and to handle unknown methods.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.hubConnection.methods = new Proxy(methods, {
      get: (target: object, p: keyof typeof target) => {
        // Register a handler if one does not exist.
        if (target[p] === undefined) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          target[p] = [this.onProcessMessage.bind(this)];
        }

        return target[p];
      },
    });

    this.hubConnection.onclose((error) => {
      this.emit('close', error).catch(this.boundEmitError);
      this.session.destroy();
    });

    this.hubConnection.onreconnecting((error) => {
      this.session.destroy();
      this.emit('reconnecting', error).catch(this.boundEmitError);
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.retryDelays = [...(this.constructor as typeof Signalr).DEFAULT_RETRY_DELAYS];
      this.emit('reconnected', connectionId).catch(this.boundEmitError);
      this.session.initialize().catch(this.boundEmitError);
    });

    if (this.connection.isHybrid) {
      this.registerHybridMessages();
    }

    this.hubConnection.on('ProcessMessage', this.onProcessMessage.bind(this));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.session as any) = new SignalrSession(this.connection.sessionId, this.hubConnection, this.connection.isHybrid);
    this.session.on('expired', () => this.emit('sessionExpired'));

    try {
      await this.hubConnection.start();
      await this.session.initialize();
    }
    catch (e) {
      const error = e as Error;
      throw new Error(`Unable to establish SignalR connection with ${this.hubConnection.baseUrl}. ${error.message}`);
    }

    return this.session;
  }

  async stop(): Promise<void> {
    if (this.isConnected) {
      await this.hubConnection.stop();
    }

    this.session.destroy();
  }
}
